import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, message, Tag, Modal, Form, Input, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  getHuifuBankCardList,
  addHuifuBankCard,
  updateHuifuBankCard,
  deleteHuifuBankCard,
  getUsers,
  downloadHuifuBankCardTemplate,
  batchImportHuifuBankCards,
} from '../../api';
import ListToolbar from '../../components/ListToolbar';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';
import { getProvinceName, getCityName, provinceMap, cityMap } from '../../utils/regionCodeMap';
import { showBatchImportResult, showBatchImportHttpError } from '../../utils/batchImportModal';

const HuifuBankCardList = () => {
  const [bankCardData, setBankCardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [displayColumns, setDisplayColumns] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBankCard, setEditingBankCard] = useState(null);
  const [form] = Form.useForm();
  const [userList, setUserList] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const fileInputRef = useRef(null);
  // 不再需要加载数据，映射已内嵌在JS文件中

  // 获取汇付银行卡列表
  const fetchData = async (search = '', page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await getHuifuBankCardList(search, page, pageSize);
      if (res?.success && Array.isArray(res.data)) {
        setBankCardData(res.data);
        setPagination({
          current: res.page || page,
          pageSize: res.page_size || pageSize,
          total: res.total || 0,
        });
        message.success(`加载成功 ${res.data.length} 条数据`);
      } else {
        setBankCardData([]);
        message.error('数据格式错误');
      }
    } catch (err) {
      console.error('加载汇付银行卡失败：', err);
      message.error('加载汇付银行卡失败，请检查接口连接');
      setBankCardData([]);
    } finally {
      setLoading(false);
    }
  };

  // 加载用户列表
  const loadUserList = async () => {
    try {
      const res = await getUsers();
      const users = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setUserList(users);
    } catch (err) {
      console.error('加载用户列表失败：', err);
    }
  };

  // 获取当前省份下的城市列表
  const getCitiesByProvince = (provinceCode) => {
    if (!provinceCode) return [];
    const provCode = String(provinceCode).trim();
    return Object.keys(cityMap)
      .filter(code => code.startsWith(provCode.substring(0, 2)))
      .map(code => ({
        value: code,
        label: cityMap[code]
      }));
  };

  // 初始化加载
  useEffect(() => {
    fetchData();
    loadUserList();
  }, []);

  // 搜索处理
  const handleSearch = () => {
    fetchData(searchText, 1, pagination.pageSize);
  };

  // 重置搜索
  const handleReset = () => {
    setSearchText('');
    fetchData('', 1, pagination.pageSize);
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchData(searchText, pagination.current, pagination.pageSize);
  };

  // 导出数据
  const handleExport = () => {
    const exportData = bankCardData.map(card => ({
      'ID': card.id,
      '用户ID': card.user_id,
      '银行名称': card.bank_name || '-',
      '开户名': card.bank_account_name || '-',
      '银行卡号': card.bank_account_no || '-',
      '银行编码': card.bank_code || '-',
      '省': getProvinceName(card.prov_id),
      '市': getCityName(card.area_id),
      '省份代码': card.prov_id || '-',
      '城市代码': card.area_id || '-',
      '是否默认': card.is_default === 1 ? '是' : '否',
      '银行卡状态': card.card_status === 1 ? '正常' : '已注销',
      '创建时间': card.created_at || '-',
      '更新时间': card.updated_at || '-',
    }));

    if (exportToCSV(exportData, '汇付银行卡列表')) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadHuifuBankCardTemplate();
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败：', error);
      message.error('下载模板失败，请重试');
    }
  };

  const handleBatchImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      message.error('请选择 CSV 文件');
      return;
    }
    try {
      setLoading(true);
      const res = await batchImportHuifuBankCards(file);
      showBatchImportResult(res, { entityName: '汇付银行卡' });
      if (res?.success) {
        fetchData(searchText, pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('批量导入失败：', error);
      showBatchImportHttpError(error);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // 分页变化处理
  const handleTableChange = (newPagination) => {
    fetchData(searchText, newPagination.current, newPagination.pageSize);
  };

  // 打开新增/编辑弹窗
  const showModal = (bankCard = null) => {
    setEditingBankCard(bankCard);
    if (bankCard) {
      form.setFieldsValue({
        user_id: bankCard.user_id,
        bank_name: bankCard.bank_name,
        bank_account_name: bankCard.bank_account_name,
        bank_account_no: bankCard.bank_account_no,
        bank_code: bankCard.bank_code,
        prov_id: bankCard.prov_id,
        area_id: bankCard.area_id,
        is_default: bankCard.is_default,
        card_status: bankCard.card_status,
      });
      setSelectedProvince(bankCard.prov_id);
    } else {
      form.resetFields();
      setSelectedProvince(null);
    }
    setIsModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingBankCard) {
        // 编辑
        const res = await updateHuifuBankCard(editingBankCard.id, values);
        if (res?.success) {
          message.success('编辑汇付银行卡成功');
          fetchData(searchText, pagination.current, pagination.pageSize);
        } else {
          message.error('编辑汇付银行卡失败：' + (res?.msg || '后端返回异常'));
        }
      } else {
        // 新增
        const res = await addHuifuBankCard(values);
        if (res?.success) {
          message.success('新增汇付银行卡成功');
          fetchData(searchText, pagination.current, pagination.pageSize);
        } else {
          message.error('新增汇付银行卡失败：' + (res?.msg || '后端返回异常'));
        }
      }
      setIsModalVisible(false);
    } catch (err) {
      console.error('提交失败：', err);
      if (err.errorFields) {
        message.error('请检查输入的数据');
      } else {
        message.error('提交失败，请检查输入或接口连接');
      }
    } finally {
      setLoading(false);
    }
  };

  // 删除银行卡
  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除该汇付银行卡吗？删除后不可恢复！')) {
      return;
    }

    try {
      setLoading(true);
      const res = await deleteHuifuBankCard(id);
      if (res?.success) {
        message.success('删除汇付银行卡成功');
        fetchData(searchText, pagination.current, pagination.pageSize);
      } else {
        message.error('删除汇付银行卡失败：' + (res?.msg || '后端返回异常'));
      }
    } catch (err) {
      console.error('删除失败：', err);
      const errMsg = err.response?.data?.msg || err.message || '删除汇付银行卡失败：网络/接口问题';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // 处理省份选择变化
  const handleProvinceChange = (provinceCode) => {
    setSelectedProvince(provinceCode);
    form.setFieldsValue({ area_id: undefined }); // 清空城市选择
  };

  // 原始表格列配置
  const originalColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 100 },
    { title: '银行名称', dataIndex: 'bank_name', key: 'bank_name', width: 150 },
    { title: '开户名', dataIndex: 'bank_account_name', key: 'bank_account_name', width: 120 },
    { 
      title: '银行卡号', 
      dataIndex: 'bank_account_no', 
      key: 'bank_account_no', 
      width: 200,
      render: (text) => {
        if (!text) return '-';
        // 显示完整银行卡号
        return text;
      }
    },
    { title: '银行编码', dataIndex: 'bank_code', key: 'bank_code', width: 120 },
    { 
      title: '省', 
      key: 'province_name', 
      width: 120,
      render: (_, record) => {
        const provId = record.prov_id;
        const provinceName = getProvinceName(provId);
        // 调试信息（仅在开发环境）
        if (process.env.NODE_ENV === 'development' && provId && provinceName === String(provId)) {
          console.log('省编码未匹配：', provId, '类型：', typeof provId);
        }
        return provinceName;
      }
    },
    { 
      title: '市', 
      key: 'city_name', 
      width: 120,
      render: (_, record) => {
        const areaId = record.area_id;
        const cityName = getCityName(areaId);
        // 调试信息（仅在开发环境）
        if (process.env.NODE_ENV === 'development' && areaId && cityName === String(areaId)) {
          console.log('市编码未匹配：', areaId, '类型：', typeof areaId);
        }
        return cityName;
      }
    },
    { title: '省份代码', dataIndex: 'prov_id', key: 'prov_id', width: 100 },
    { title: '城市代码', dataIndex: 'area_id', key: 'area_id', width: 100 },
    { 
      title: '是否默认', 
      dataIndex: 'is_default', 
      key: 'is_default', 
      width: 100,
      render: (isDefault) => (
        <Tag color={isDefault === 1 ? 'blue' : 'default'}>
          {isDefault === 1 ? '是' : '否'}
        </Tag>
      )
    },
    { 
      title: '银行卡状态', 
      dataIndex: 'card_status', 
      key: 'card_status', 
      width: 120,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '正常' : '已注销'}
        </Tag>
      )
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160 },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} size="small" onClick={() => showModal(record)}>编辑</Button>
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  // 从localStorage加载列设置并应用到表格
  useEffect(() => {
    const storageKey = 'huifuBankCardList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};
    
    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }
    
    const processedColumns = originalColumns.map(col => {
      const state = columnStates[col.key];
      if (state === 'hidden') {
        return null;
      }
      return col;
    }).filter(col => col !== null);
    
    setDisplayColumns(processedColumns);
  }, []);

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="汇付银行卡管理" />
      
      <ListToolbar
        searchPlaceholder="搜索用户ID、银行名称、开户名"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        showBatchDelete={false}
        showAdd={true}
        onAdd={() => showModal()}
        actions={[
          {
            key: 'download-template',
            label: '下载模板',
            icon: <DownloadOutlined />,
            onClick: handleDownloadTemplate,
            type: 'default',
          },
          {
            key: 'batch-import',
            label: '批量导入',
            icon: <UploadOutlined />,
            onClick: handleBatchImport,
            type: 'default',
          },
        ]}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <ResizableTable
        columns={displayColumns.length > 0 ? displayColumns : originalColumns}
        dataSource={bankCardData}
        rowKey="id"
        loading={loading}
        storageKey="huifuBankCardList"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1500 }}
        locale={{ emptyText: '暂无汇付银行卡数据' }}
        bordered
        style={{ background: '#fff' }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingBankCard ? '编辑汇付银行卡' : '新增汇付银行卡'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={600}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="user_id"
            label="用户ID"
            rules={[{ required: !editingBankCard, message: '请输入用户ID' }]}
          >
            <Select
              placeholder="选择用户"
              disabled={!!editingBankCard}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={userList.map(user => ({
                value: user.id,
                label: `${user.nickname || '未命名'} (ID: ${user.id}, 手机: ${user.phone || '无'})`
              }))}
            />
          </Form.Item>
          <Form.Item
            name="bank_name"
            label="银行名称"
          >
            <Input placeholder="例如：中国工商银行" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="bank_account_name"
            label="开户名"
            rules={[{ required: true, message: '请输入开户名' }]}
          >
            <Input placeholder="请输入开户名" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="bank_account_no"
            label="银行卡号"
            rules={[{ required: true, message: '请输入银行卡号' }]}
          >
            <Input placeholder="请输入银行卡号" maxLength={32} />
          </Form.Item>
          <Form.Item
            name="bank_code"
            label="银行编码"
          >
            <Input placeholder="例如：ICBC" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="prov_id"
            label="省份"
            rules={[{ required: true, message: '请选择省份' }]}
          >
            <Select
              placeholder="选择省份"
              onChange={handleProvinceChange}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={Object.keys(provinceMap).map(code => ({
                value: code,
                label: provinceMap[code]
              }))}
            />
          </Form.Item>
          <Form.Item
            name="area_id"
            label="城市"
            rules={[{ required: true, message: '请选择城市' }]}
          >
            <Select
              placeholder="先选择省份"
              disabled={!selectedProvince}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={getCitiesByProvince(selectedProvince)}
            />
          </Form.Item>
          <Form.Item
            name="is_default"
            label="是否默认"
            initialValue={0}
          >
            <Select>
              <Select.Option value={0}>否</Select.Option>
              <Select.Option value={1}>是</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="card_status"
            label="银行卡状态"
            initialValue={1}
          >
            <Select>
              <Select.Option value={1}>正常</Select.Option>
              <Select.Option value={0}>已注销</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HuifuBankCardList;

